import React, { useState, useEffect, useCallback } from "react";

import {
  Paper,
  Container,
  Typography,
  Box,
  Button,
  Grid,
} from "@material-ui/core";
import { useForm, useWatch } from "react-hook-form";

import { Spinner } from "@atoms";
import FormBuilder from "@containers/components/FormBuilder/FormBuilder";
import useApiFetch from "@hooks/useApiFetch";
import useQueryParams from "@hooks/useQueryParams";
import Loading from "@shared/components/Loading";

import formConfig from "../Config/IncomeActivationConfig";
import states from "../Config/states";

const IncomeActivation = ({
  match: {
    params: { id: reaccountId },
  },
}) => {
  const { getQueryParams } = useQueryParams();
  const token = getQueryParams("rkcToken");
  sessionStorage.setItem("rkcToken", token);
  const [apiResponse, setApiResponse] = useState([]);

  const [loading, setLoading] = useState(true);
  const form = useForm({
    mode: "onChange",
    defaultValues: {
      beneficiaries: [],
    },
  });

  const { get, post, isPending } = useApiFetch();

  const getPolicyNumber = useCallback(async () => {
    try {
      const response = await get(
        `rkc/GetREAccountInfo?reAccountID=${reaccountId}`
      );
      if (response?.data) {
        setApiResponse(response.data);
      } else {
        console.warn("No data returned");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getPolicyNumber();
  }, [getPolicyNumber]);

  const policyOptions = Array.isArray(apiResponse)
    ? apiResponse.map((item) => ({
        label: `${item.policyNumber}-${item.taxCode}`,
        value: `${item.policyNumber}-${item.taxCode}`,
      }))
    : [];

  // Create the new config object
  const newConfig = {
    name: "policyNumber",
    label: "Policy Number",
    type: "multiSelect",
    options: policyOptions,
    required: true,
    isMulti: true,
  };

  // Assuming `existingConfig` is your current config array
  const updatedConfig = [newConfig, ...formConfig];

  const {
    register,
    control,
    watch,
    handleSubmit,
    setValue,
    getValues,
    setError,
    clearErrors,
    reset,
    formState: { errors },
  } = form;

  const saveData = (data) => {
    const msgs = [];
    let hasError = false;
    if (Array.isArray(data.beneficiaries) && data.beneficiaries.length > 0) {
      const beneTypes = new Map();
      for (const beneficiary of data.beneficiaries) {
        const beneType = (beneficiary?.beneficiaryType ?? "").toString().trim(); // allow empty/unspecified
        if (!beneTypes.has(beneType)) beneTypes.set(beneType, []);
        beneTypes.get(beneType).push(beneficiary);
      }

      const EPS = 0.0001; // to handle floating sums like 33.33*3
      for (const [type, rows] of beneTypes.entries()) {
        // sum percentages (non-numeric treated as 0)
        const total = rows.reduce(
          (sum, r) => sum + (Number(r?.percentage) || 0),
          0
        );

        if (Math.abs(total - 100) > EPS) {
          const label = type || "(unspecified type)";
          hasError = true;
          msgs.push(
            `For ${label}, total must be 100 (current ${Number(total.toFixed(2))}).`
          );
        }
      }
      setPercentageErrors(msgs);
    }
    if (hasError) return;
    setPercentageErrors(msgs);
    const formData = getValues();

    const getFriendlyContentType = (mimeType) => {
      const map = {
        "application/pdf": "PDF",
        "image/jpeg": "JPEG",
        "image/jpg": "JPG",
        "image/png": "PNG",
        "image/gif": "GIF",
        "image/bmp": "BMP",
      };
      return map[mimeType] || "Unknown";
    };

    const policyAccountNumbers = {};

    formData.policyNumber.forEach((policy) => {
      const key = policy.split("-")[0]; // Extract part before "-"
      const match = apiResponse.find((item) => item.policyNumber === key);
      policyAccountNumbers[key] = match?.reAccountID || "";
    });

    const formInstance = [
      {
        documentType: "Drivers License",
        contents: formData.fileUpload2?.base64 || "",
        fileName: formData.fileUpload2?.fileName || "fileUpload2.pdf",
        contentType:
          getFriendlyContentType(formData.fileUpload2?.contentType) || "PDF",
      },
    ];

    if (formData.fileUpload1?.base64) {
      formInstance.push({
        documentType: "Drivers License",
        contents: formData.fileUpload1.base64,
        fileName: formData.fileUpload1.fileName || "fileUpload1.pdf",
        contentType:
          getFriendlyContentType(formData.fileUpload1.contentType) || "PDF",
      });
    }

    const apiRequestBody = {
      policyAccountNumbers,
      carrierCode: "90611", // Assuming this is static
      reAccountNumber: 1111111111, // Replace with actual value if dynamic
      reasonCode: "Income", // Assuming this is static or can be mapped
      beneficiaries: formData.beneficiaries.map((b) => ({
        beneficiaryClientType:
          b.category === "Individual" ? "Individual" : "Organization",
        beneficiaryOrgType: b.category === "Individual" ? "" : b.category,
        beneficiaryType: b.beneficiaryType,
        beneficiaryFirstName: b.firstName,
        beneficiaryLastName: b.lastName,
        beneficiaryFullName:
          b.category === "Individual"
            ? `${b.firstName} ${b.lastName}`
            : b.entityName,
        beneficiaryBirthDate: b.birthdate,
        beneficiaryGovtId: b.ssn?.replace(/-/g, "") || "",
        beneficiaryTrustType: b.trustType,
        beneficiaryRelationship: b.relationship,
        beneficiaryPercentage: b.percentage,
      })),
      formInstance,
      incomePaymentType: "Maximum", // Assuming static
      incomePercentage: 1, // Replace with actual if needed
      incomeAmount: 1, // Replace with actual if needed
      incomeFrequency: formData.frequency,
      incomeCoveredPerson: formData.factorName + " Life", // Assuming static
      incomeJointFirst: formData.jointFirstName,
      incomeJointMiddle: formData.jointMiddleInitial,
      incomeJointLast: formData.jointLastName,
      incomeJointGender: formData.jointGender,
      incomeJointDOB: formData.jointDOB,
      incomeJointAddress1: formData.spouseAddressLine1,
      incomeJointAddress2: formData.spouseAddressLine2,
      incomeJointCity: formData.spouseAddressCity,
      incomeJointState:
        states.find((s) => s.label === formData.spouseAddressState)?.value ||
        "",
      incomeJointZip: formData.spouseAddressZip,
      bankAccountType: formData.accountType,
      bankAccountNumber: formData.accountNumber,
      bankRoutingNumber: formData.routingNumber,
      bankAccountHolderName: formData.accountHolder,
      bankName: formData.bankName,
      bankMethod: formData.paymentLocation === "ACH" ? "ACH" : "",
    };
    createPolicy(apiRequestBody);
    reset();
  };

  const createPolicy = useCallback(async (values) => {
    await post(`rkc/reaccounts/policyadmin`, JSON.stringify(values), {
      closeWindow: true,
    });
  }, []);

  const [percentageErrors, setPercentageErrors] = useState([]);

  return loading ? (
    <Loading loading={loading} />
  ) : (
    <Spinner requesting={isPending}>
      <Container
        style={{
          maxWidth: 1430,
          margin: "0 auto",
          maxHeight: 900,
          overflowY: "auto",
        }}
      >
        {/* Bordered Form Section */}
        <Paper
          elevation={3}
          style={{
            padding: 24,
            border: "1px solid #ccc",
            borderRadius: 8,
            marginBottom: 32,
            background: "#F0F8FF",
          }}
        >
          <form onSubmit={handleSubmit(saveData)}>
            <Typography
              variant="h4"
              align="center"
              color="blue"
              style={{ marginBottom: 10 }}
            >
              Allianz Income Activation Form
            </Typography>

            <FormBuilder
              formConfig={updatedConfig}
              form={form}
              submitErrors={percentageErrors}
            />

            <Grid container>
              <Grid item xs={9} style={{ display: "flex" }}>
                <input
                  type="checkbox"
                  style={{ height: "20px", width: "20px" }}
                />
                <p style={{ margin: "0px", marginLeft: "10px" }}>
                  By checking this box I am consenting to e-signature.
                </p>
              </Grid>
              <Grid item xs={3}>
                <Button type="submit" variant="contained" color="primary">
                  Submit
                </Button>
              </Grid>
            </Grid>
          </form>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            marginTop={4}
            paddingTop={2}
            borderTop="1px solid #ddd"
          >
            <Typography variant="body2" color="textSecondary">
              © 2025 Retirement Edge Solutions
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Spinner>
  );
};

export default IncomeActivation;
